// One plan, and the four places you can be inside it.
//
// The private chat, the conversation, the decisions, the people. Pending actions are a
// fifth surface and have no tab, because they render inline in the group conversation
// where they were raised.
//
// One `usePlan` for all four, so switching tabs is not a refetch and a signal that arrives
// while the decisions are on screen updates the conversation behind it. The whole plan is
// read at once because the whole plan is small and because five reads that can disagree
// with each other is five reads that will. The private thread is the one piece read
// outside that pipeline — see `usePrivateThread` — because it is a second thread, not a
// second plan.
//
// The refusal path is worth reading. A 404 from this API means: no such plan, or not
// yours, or not a participant, or a malformed id — deliberately indistinguishable, because
// telling them apart would confirm that a preparing thread exists. So this screen says the
// one true thing that covers all of them and does not guess which it was.
//
// Only that considered no earns the sentence. A 401 is a stale bearer waiting on the
// vendor's next refresh, not a withheld plan, so it reads as still signing in — `usePlan`
// re-reads when the session store publishes a fresh token or the network comes back.
//
// ---------------------------------------------------------------------------
// REACHABILITY IS PRESENTATION (docs/archived/13-visual-refresh.md, docs/archived/09-web-client.md)
// ---------------------------------------------------------------------------
//
// The decisions and the people used to be tabs with their own routes. They still have
// routes — `tab` still carries `'decisions'` and `'people'`, and direct links to them keep
// working — but neither swaps out the conversation column any more: both are sections of
// one plan panel (`Decisions.tsx`, holding the objective, the roster, and the decisions),
// which is a persistent panel on desktop, a flex sibling of the conversation
// (`DecisionsPanel`), and a closed strip above the composer on mobile
// (`DecisionsMobileStrip`, handed to whichever of `Conversation`/`PrivateChat` is rendering
// so it lands inside their own footer, above the composer, rather than beside the
// conversation column) — both rendered on every room so `09-web-client.md`'s "visible ...
// at any time" holds without a second navigation.
//
// `tab === 'decisions'` or `tab === 'people'` forces that panel open on mount (it otherwise
// starts collapsed), so a direct link to either old route still shows the roster or the
// decisions without an extra click — the panel holds both now, so either tab opens the same
// panel.
//
// Private chat, the co-principals' room and the group conversation are still exactly one
// thread each, chosen the same way as before; only their presentation changed, from a text
// tab row to `RoomSwitcher`'s icon rail.

import { lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client.js';
import { DecisionsMobileStrip } from '../components/DecisionsMobileStrip.js';
import { DecisionsPanel } from '../components/DecisionsPanel.js';
import { DemoWelcome } from '../components/DemoWelcome.js';
import { RoomSwitcher } from '../components/RoomSwitcher.js';
import { useDemoSeatIntro } from '../demo/useDemoSeatIntro.js';
import { principalsRoomLabel } from '../format.js';
import { defaultInternals, type Internals } from '../internals.js';
import { groupThreadOf, ownThreadOf, principalsThreadOf } from '../plan/snapshot.js';
import { usePlan } from '../plan/usePlan.js';
import { Conversation } from './Conversation.js';
import { PrivateChat } from './PrivateChat.js';

// Vite removes this import and its chunk from builds where the operator flag is absent.
const OperatorPanel =
  import.meta.env.VITE_MOR_OPERATOR_ENABLED === 'true'
    ? lazy(() =>
        import('../operator/DebugPanel.js').then((module) => ({
          default: module.DebugPanel,
        })),
      )
    : null;

export type PlanTab = 'private' | 'principals' | 'conversation' | 'decisions' | 'people';

export type PlanScreenProps = {
  client: ApiClient;
  tab: PlanTab;
  internals?: Internals;
  live?: boolean;
  pollMs?: number;
};

export function PlanScreen({
  client,
  tab,
  internals,
  live,
  pollMs,
}: PlanScreenProps): JSX.Element {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const chosen = internals ?? defaultInternals;
  const state = usePlan({
    client,
    planId: planId ?? '',
    internals: chosen,
    ...(live === undefined ? {} : { live }),
    ...(pollMs === undefined ? {} : { pollMs }),
  });
  // Hooked here, unconditionally, rather than after the status checks below — `PlanScreen`
  // returns early for several statuses, and a hook cannot follow a conditional return.
  // `readDemoSource` for a plan that turns out not to be a demo (or has not loaded yet)
  // costs nothing: the hook itself no-ops until `isDemo` is true.
  const demoSeat = useDemoSeatIntro(
    client,
    planId ?? '',
    state.snapshot?.plan.isDemo ?? false,
  );

  if (state.status === 'loading') {
    return (
      <main className="screen screen--plan">
        <p className="screen__loading">Opening…</p>
      </main>
    );
  }

  if (state.status === 'waiting') {
    return (
      <main className="screen screen--plan">
        <p className="screen__loading">Signing you back in…</p>
      </main>
    );
  }

  if (state.status === 'faltered') {
    return (
      <main className="screen screen--plan">
        <p className="screen__problem">That didn’t load. It will keep trying.</p>
      </main>
    );
  }

  if (state.status === 'refused') {
    return (
      <main className="screen screen--plan">
        <p className="screen__problem">This plan isn’t available to you.</p>
      </main>
    );
  }

  if (state.status === 'unreachable') {
    return (
      <main className="screen screen--plan">
        <p className="screen__problem">
          Couldn’t reach the server. Try again in a moment.
        </p>
      </main>
    );
  }

  const snapshot = state.snapshot;
  const base = `/plans/${planId ?? ''}`;
  const groupThread = snapshot === null ? null : groupThreadOf(snapshot.threads);
  const principalsThread =
    snapshot === null ? null : principalsThreadOf(snapshot.threads);
  const ownThread = snapshot === null ? null : ownThreadOf(snapshot.threads);
  const people = snapshot === null ? [] : snapshot.people;
  // Named only when the whole roster is exactly two people — the one shape where this
  // client can actually verify who the co-principal is, rather than guessing at a roster
  // this API's contract does not label with roles. See `format.ts`'s `namedCoPrincipal`
  // for the full reasoning. Every other roster shape falls back to a role-neutral phrase
  // that names nobody.
  const principalsLabel = principalsRoomLabel(people);
  // Built once and handed to whichever room is showing — see the header comment above and
  // `DecisionsMobileStrip.tsx`'s for why this has to be rendered inside `Conversation`'s /
  // `PrivateChat`'s own footer rather than as a `.planBody` sibling.
  const decisionsStrip =
    snapshot === null ? null : (
      <DecisionsMobileStrip
        state={state}
        internals={chosen}
        client={client}
        planId={planId ?? ''}
      />
    );

  // The landing route (`/plans/:planId`) switches to the group conversation only once
  // that thread exists. Before then it lands in the principals conversation, when there
  // is one, or the reader's private chat. The thread itself is the fact; inferring this
  // from plan state would make a partially loaded or inconsistent snapshot choose a room
  // the caller may not have.
  const landing =
    tab === 'conversation' || tab === 'decisions' || tab === 'people'
      ? groupThread === null
        ? principalsThread === null
          ? 'private'
          : 'principals'
        : 'conversation'
      : tab;

  const screen = (
    <main className="screen screen--plan">
      {/* Inside `screen--plan`, not a full-width row above it: this is what puts the plan
          panel's own left border flush against the header from the very top, since the
          panel and this whole column both start at the same point below it. The name has
          nothing else sharing its row to be pushed around, so it never moves when the
          panel opens or closes — only the row's own width changes. */}
      <header className="planHead">
        {/* The mark sits with the NAME, not with the controls: read together they say what
            they mean — "The blended family, Demo" — where a pill floating beside a button
            read as one more thing to press. */}
        <h1 className="planHead__name">
          {snapshot?.plan.name ?? ''}
          {snapshot?.plan.isDemo ? <span className="demoMark">Demo</span> : null}
        </h1>
        {/* The "about" drawer (`DecisionsPanel.tsx`) carries this now, as a second tab
            beneath the decisions tab — not a link here, which used to sit jammed against
            that tab in this same corner. */}
      </header>
      {snapshot?.plan.isDemo && demoSeat !== null ? (
        <DemoWelcome planId={planId ?? ''} seat={demoSeat} />
      ) : null}
      <div className="planBody">
        <RoomSwitcher
          base={base}
          landing={landing}
          ownThread={ownThread}
          principalsThread={principalsThread}
          groupThread={groupThread}
          principalsRoomLabel={principalsLabel}
        />

        <div className="planBody__main">
          {landing === 'private' ? (
            <PrivateChat
              client={client}
              thread={ownThread}
              state={state}
              internals={chosen}
              decisionsStrip={decisionsStrip}
            />
          ) : null}
          {landing === 'principals' ? (
            <PrivateChat
              client={client}
              thread={principalsThread}
              state={state}
              internals={chosen}
              // "Principals" is internal terminology and never appears as rendered copy,
              // aria-label included — see `RoomSwitcher.tsx`'s header comment and
              // acceptance criterion 2 in `docs/archived/13-visual-refresh.md`.
              label={`Conversation with ${principalsLabel}`}
              emptyText={`Nothing has been said with ${principalsLabel} yet.`}
              roomKind="principals"
              decisionsStrip={decisionsStrip}
            />
          ) : null}
          {landing === 'conversation' ? (
            <Conversation
              state={state}
              internals={chosen}
              decisionsStrip={decisionsStrip}
            />
          ) : null}
        </div>
      </div>
    </main>
  );

  // The thread backing whichever tab is actually showing (`landing`, not `tab` — before the
  // group thread exists, `conversation` still lands in a private room). Decisions and People
  // back no thread at all, and the panel is told that plainly rather than guessing one.
  const debugThread =
    landing === 'private'
      ? ownThread
      : landing === 'principals'
        ? principalsThread
        : landing === 'conversation'
          ? groupThread
          : null;

  return (
    <div className="planPage">
      {/* The debug rail sits beside the plan column, not over it, because a popover over the
          conversation is exactly the surface a grieving reader is trying to look at. It's a
          `.planLayout` sibling of `screen` and the plan panel, both starting at the same
          point right below the header. Flag-gated because a family build carries no
          operator panel at all. */}
      <div className="planLayout">
        {OperatorPanel === null ? null : (
          <Suspense fallback={null}>
            <OperatorPanel
              client={client}
              planId={planId ?? ''}
              threadId={debugThread?.id ?? null}
            />
          </Suspense>
        )}
        {screen}
        {snapshot === null ? null : (
          <DecisionsPanel
            state={state}
            internals={chosen}
            client={client}
            planId={planId ?? ''}
            initialOpen={tab === 'decisions' || tab === 'people'}
            isDemo={snapshot?.plan.isDemo ?? false}
            demoSeat={demoSeat}
          />
        )}
      </div>
    </div>
  );
}
